<template>
  <v-data-table
    :items="items.filter(x => x.use_inventory_link === false)"
    :headers="getHeaders"
    :search="search"
    :mobile-breakpoint="0"
    :items-per-page="$vuetify.breakpoint.xsOnly ? items.length + 1 : 25"
    hide-default-footer
    sort-by="quantity_needed"
    dense
    @page-count="pageCount = $event"
    :page.sync="page"
  >
    <template v-slot:item.nom="{ item }">
      <v-row align="center" no-gutters class="f-nw">
        <v-tooltip bottom v-if="item.orders.length > 0">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" class="mr-2" color="warning">fa-truck</v-icon>
          </template>
          <div>
            <span>Déja commandé le</span>
            <v-row
              v-for="order in item.orders"
              :key="order.id"
              dense
              no-gutters
            >
              <span class="mr-2">{{ order.created | shortLocalFormat }}:</span>
              <span class="ml-1">{{ Number(order.quantity_ordered) }}</span>
              <span class="ml-1 caption">{{ getUnit(item) }}</span>
            </v-row>
          </div>
        </v-tooltip>

        <span class="font-capitalize grey--text text--darken-2">
          {{
            item.data !== undefined && item.data.order_name !== null
              ? item.data.order_name
              : item.nom
          }}</span
        >
      </v-row>
    </template>
    <template v-slot:item.quantity_inventory="{ item }">
      <v-row align="center" no-gutters dense>
        <span>{{ item.quantity_inventory }}</span>
        <v-tooltip bottom v-if="item.orders.length > 0">
          <template v-slot:activator="{ on }">
            <span
              v-on="on"
              class="caption ml-1"
              style="color: #ff8200;"
              v-if="item.orders.length > 0"
              >({{ getProjectedValue(item) }})</span
            >
          </template>
          Quantité en inventaire avec les commandes en route
        </v-tooltip>
      </v-row>
    </template>
    <template v-slot:item.quantity_ordered="{ item }">
      <v-row align="center" justify="end" no-gutters>
        <j-decimal-input
          hide-details
          filled
          single-line
          dense
          unit-suffix
          class="no-before my-1"
          v-model="item.quantity_ordered"
          :suffix="getUnit(item)"
          style="max-width: 200px;"
        ></j-decimal-input>
      </v-row>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-row
        align="center"
        justify="center"
        no-gutters
        v-if="item.quantity_needed < 0"
      >
        <v-hover v-slot:default="{ hover }" v-if="item.quantity_ordered < 1">
          <v-btn icon @click="addItem(item)">
            <v-icon v-if="!hover">far fa-square</v-icon>
            <v-icon v-else>fa-plus-square</v-icon>
          </v-btn>
        </v-hover>
        <v-hover v-slot:default="{ hover }" v-else>
          <v-btn icon @click="removeItem(item)">
            <v-icon v-if="!hover">fa-square</v-icon>
            <v-icon v-else>fa-minus-square</v-icon>
          </v-btn>
        </v-hover>
      </v-row>
    </template>
    <template v-slot:header.actions>
      <v-row align="center" justify="center" no-gutters>
        <v-hover large v-slot:default="{ hover }" v-if="!orderedAll">
          <v-btn color="success" icon @click="orderAll">
            <v-icon v-if="!hover && getIntermediateState"
              >far fa-check-square</v-icon
            >
            <v-icon v-else-if="!hover">far fa-square</v-icon>
            <v-icon v-else>fa-plus-square</v-icon>
          </v-btn>
        </v-hover>
        <v-hover v-slot:default="{ hover }" v-else>
          <v-btn large color="success" icon @click="removeAll">
            <v-icon v-if="!hover">fa-square</v-icon>
            <v-icon v-else>fa-minus-square</v-icon>
          </v-btn>
        </v-hover>
      </v-row>
    </template>
    <template v-slot:item.quantity_needed="{ item }">
      <v-row align="center" no-gutters class="f-nw">
        <span
          v-if="item.quantity_needed && item.quantity_needed !== 0"
          :style="{ color: item.quantity_needed < 0 ? 'error' : '' }"
          >{{ Math.abs(item.quantity_needed) }}</span
        >
        <span
          class="caption ml-1"
          v-if="item.quantity_needed && item.quantity_needed !== 0"
          >{{ item.data.unite_mesure_inventory }}</span
        >
        <v-tooltip left v-if="item.is_order_by_inventory_link === true">
          <template v-slot:activator="{ on }">
            <v-icon small class="ml-2" v-on="on">fa-link</v-icon>
          </template>
          <v-row
            v-for="link in item.order_by_inventory_link"
            :key="link.recipe.id"
            no-gutters
            dense
            justify="space-between"
          >
            <span>{{ link.recipe.nom }} :</span>
            <span class="ml-2">{{ Math.abs(link.qty) }}</span>
          </v-row>
        </v-tooltip>
      </v-row>
    </template>
    <template v-slot:item.quantity_minimum="{ item }">
      <span v-if="item.is_order_by_inventory_link === false">{{
        item.quantity_minimum
      }}</span>
    </template>
    <template v-slot:item.quantity_maximum="{ item }">
      <span v-if="item.is_order_by_inventory_link === false">{{
        item.quantity_maximum
      }}</span>
    </template>
    <template v-slot:item.quantity_by_sale="{ item }">
      <span v-if="item.is_order_by_inventory_link === false">{{
        item.quantity_by_sale
      }}</span>
    </template>
    <template v-slot:top>
      <v-row align="center" justify="end" dense>
        <v-col cols="12" sm="6">
          <v-text-field
            dense
            filled
            hide-details
            single-line
            clearable
            prepend-inner-icon="fa-search"
            v-model="search"
            class="no-before"
          ></v-text-field>
        </v-col>
      </v-row>
    </template>
    <template v-slot:footer>
      <v-pagination
        color="accent"
        v-model="page"
        :length="pageCount"
        :total-visible="5"
      ></v-pagination>
    </template>
  </v-data-table>
</template>

<script>
import { mappers } from "@/mappers";
import { mapGetters } from "vuex";
import { round10 } from "round10";
export default {
  name: "DeliverySlipCreatorGrid",
  props: {
    sales_target: {
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    date: {
      required: true
    }
  },
  computed: {
    ...mapGetters(["getProducts", "getInventoryRecords"]),
    getIntermediateState() {
      const value = this.items
        .map(x => x.quantity_ordered)
        .reduce((a, b) => a + b, 0);
      return value > 0 && value !== this.items.length;
    },
    getHeaders() {
      if (this.$vuetify.breakpoint.mdAndUp) {
        if (this.sales_target !== null) {
          return [
            { value: "nom", text: "Produit", width: 200 },
            { value: "quantity_inventory", text: "Inventaire" },
            { value: "quantity_minimum", text: "Seuil" },
            { value: "quantity_maximum", text: "Max." },
            { value: "quantity_by_sale", text: "Selon ventes" },
            { value: "quantity_needed", text: "Manque" },
            { value: "quantity_ordered", text: "Commande", align: "end" },
            { value: "actions", text: "", width: 20, sortable: false }
          ];
        } else {
          return [
            { value: "nom", text: "Produit", width: 200 },
            { value: "quantity_inventory", text: "Inventaire" },
            { value: "quantity_minimum", text: "Seuil" },
            { value: "quantity_maximum", text: "Cible" },
            { value: "quantity_needed", text: "Manque" },
            { value: "quantity_ordered", text: "Commande", align: "end" },
            { value: "actions", text: "", width: 20, sortable: false }
          ];
        }
      } else {
        return [
          { value: "nom", text: "Produit", width: 200 },
          { value: "quantity_needed", text: "Manque" },
          { value: "quantity_ordered", text: "Commande", align: "end" },
          { value: "actions", text: "", width: 20, sortable: false }
        ];
      }
    }
  },
  data() {
    return {
      page: 1,
      pageCount: 0,
      orderedAll: false,
      search: "",
      headers: [
        { value: "nom", text: "Produit", width: 200 },
        { value: "quantity_inventory", text: "Inventaire" },
        { value: "quantity_minimum", text: "Seuil" },
        { value: "quantity_maximum", text: "Cible" },
        { value: "quantity_needed", text: "Manque" },
        { value: "quantity_ordered", text: "Commande", align: "end" },
        { value: "actions", text: "", width: 20, sortable: false }
      ]
    };
  },
  methods: {
    removeItem(item) {
      this.orderedAll = false;
      item.quantity_ordered = 0;
    },
    addItem(item) {
      let value = -item.quantity_needed;
      if (item.data.use_order_quantity === true) {
        value = Math.ceil(mappers.productInvToOrder(value, item.data));
      } else if (mappers.isCustomUnit(item.data.unite_mesure_inventory)) {
        value = Math.ceil(value);
      }
      item.quantity_ordered = value;
    },
    orderAll() {
      this.orderedAll = true;
      this.items.map(item => {
        this.addItem(item);
      });
    },
    removeAll() {
      this.orderedAll = false;
      this.items.map(item => {
        item.quantity_ordered = 0;
      });
    },
    getUnit(item) {
      if (item.data) {
        if (item.data.use_order_quantity) {
          return item.data.unite_mesure_order;
        } else {
          return item.data.unite_mesure_inventory;
        }
      } else {
        return "";
      }
    },
    getProjectedValue(item) {
      let value = 0;
      if (item.data.use_order_quantity === true) {
        value = Math.ceil(
          mappers.productOrderToInv(
            item.orders
              .map(o => Number(o.quantity_ordered))
              .reduce((a, b) => a + b, 0),
            item.data
          )
        );
      } else {
        value = item.orders
          .map(o => Number(o.quantity_ordered))
          .reduce((a, b) => a + b, 0);
      }
      return round10(item.quantity_inventory + value, 0);
    }
  }
};
</script>

<style></style>
